import React, { useState } from "react";
import { PageHeader, DisclaimerFooter } from "@/components/Bits";
import { useChallenge } from "@/context/ChallengeContext";
import { computeLot, fmtUSD, fmtPct } from "@/lib/calc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calculator } from "lucide-react";

export default function CalculatorPage() {
  const { data } = useChallenge();
  const balance = data?.current_balance ?? 350;

  const [form, setForm] = useState({ capital: balance, targetPct: 2.33, slPips: 50, tpPips: 50, rr: 1 });
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const calc = () => {
    setResult(
      computeLot({
        capital: Number(form.capital),
        targetPct: Number(form.targetPct),
        slPips: Number(form.slPips),
        tpPips: Number(form.tpPips),
        rr: Number(form.rr),
      })
    );
  };

  const fields = [
    { k: "capital", label: "Aktualny kapitał ($)", tid: "lot-calc-capital-input" },
    { k: "targetPct", label: "Cel na turę (%)", tid: "lot-calc-target-input" },
    { k: "slPips", label: "SL w pipsach", tid: "lot-calc-sl-input" },
    { k: "tpPips", label: "TP w pipsach", tid: "lot-calc-tp-input" },
    { k: "rr", label: "RR", tid: "lot-calc-rr-input" },
  ];

  return (
    <div>
      <PageHeader title="Kalkulator lota" subtitle="Dobierz wielkość pozycji do celu tury." />

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="glow-card rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-5">
            <Calculator className="text-violet-400" size={22} />
            <h2 className="font-display font-semibold text-lg">Parametry</h2>
          </div>
          <div className="space-y-4">
            {fields.map((f) => (
              <div key={f.k}>
                <Label className="text-xs tracking-widest text-demon-muted">{f.label}</Label>
                <Input
                  data-testid={f.tid}
                  type="number"
                  step="any"
                  value={form[f.k]}
                  onChange={set(f.k)}
                  className="mt-1.5 bg-black/50 border-violet-900/40 h-11 text-white font-mono focus-visible:ring-violet-500"
                />
              </div>
            ))}
            <Button
              data-testid="lot-calc-compute-button"
              onClick={calc}
              className="w-full h-12 font-display font-semibold bg-gradient-to-r from-violet-700 to-violet-500 border-0 shadow-[0_0_20px_rgba(168,85,247,0.35)]"
            >
              OBLICZ LOT
            </Button>
          </div>
        </div>

        <div className="glow-card rounded-2xl p-6">
          <h2 className="font-display font-semibold text-lg mb-5">Wynik</h2>
          {result ? (
            <div className="space-y-3" data-testid="lot-calc-result">
              {[
                ["Wymagany profit", fmtUSD(result.requiredProfit), "text-white"],
                ["Sugerowany lot", result.lot.toFixed(2), "text-violet-300 text-glow"],
                ["Potencjalny profit przy TP", "+" + fmtUSD(result.profitAtTp), "text-emerald-400"],
                ["Potencjalna strata przy SL", "-" + fmtUSD(result.lossAtSl), "text-red-400"],
                ["RR", "1 : " + result.rr, "text-white"],
                ["% kapitału ryzykowany", fmtPct(result.riskPct), "text-amber-400"],
              ].map(([k, v, c]) => (
                <div key={k} className="flex items-center justify-between border-b border-violet-900/20 pb-2.5">
                  <span className="text-sm text-demon-muted">{k}</span>
                  <span className={`font-mono font-bold text-lg ${c}`}>{v}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-demon-muted text-sm py-10 text-center">
              Wprowadź parametry i kliknij <span className="text-violet-400">OBLICZ LOT</span>.
            </div>
          )}
          <p className="text-[11px] text-demon-muted/70 mt-5">
            Założenie modelu: 0.10 lota = $50 przy ruchu 50 pips.
          </p>
        </div>
      </div>
      <DisclaimerFooter />
    </div>
  );
}
